import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { FaInfoCircle, FaRegCopy } from "react-icons/fa";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { Button, Switch, Text, Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import ButtonPopover from "../../components/buttons/ButtonPopover";
import CustomSelect from "../../components/forms/CustomSelect";
import RequiredInput from "../../components/forms/InputHOC";
import { Loader } from "../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../components/priviledge/PriviledgeProvider";
import useNetworks from "../../hooks/api-calls/useNetworks";
import useStates from "../../hooks/api-calls/useStates";
import useUsers from "../../hooks/api-calls/useUsers";
import useDeleteDepartment from "../../hooks/customs/departments/useDeleteDepartment";
import useFetch from "../../hooks/useFetch";
import Page from "../../layout/Page/Page";
import FormGroup from "../../layout/shared/form-group";
import { DepartmentService } from "../../services/department/departmentService";
import { ProductTypeService } from "../../services/product-type/productTypeService";
import { Department, DepartmentFieldsModel } from "../../type/entities/department-type";
import FormFooter from "../_layout/_footers/form-footer";
import CustomSelectApiHookForm from "../../components/forms/CustomSelectApiHookForms";

interface CreateFormProps {
    id: string | undefined;
    setNodeId: (value: string | undefined) => void;
    setCopyConfig: (value: boolean) => void;
    setInfo: (value: boolean) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    refetch: () => void;
}

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
    description: yup.string().nullable(),
    handlesCommunications: yup.boolean().required(),
    commercialNetworkIds: yup
        .array()
        .of(yup.string())
        .min(1, 'Debe seleccionar al menos una red comercial')
        .required('Campo obligatorio'),
    departmentUsers: yup.array().nullable(),
    departmentStatusesToView: yup.array().nullable(),
    departmentStatusesToEdit: yup.array().nullable(),
    departmentDashboardStatuses: yup.array().nullable(),
    departmentBlockedContractStatuses: yup.array().nullable(),
    departmentProductTypes: yup.array().nullable(),
});

const DepartmentForm: React.FC<CreateFormProps> = ({ id, setNodeId, setCopyConfig, setInfo, isOpen, setIsOpen, refetch }) => {

    const { userCan } = usePrivilege();
    const { getNetworksList } = useNetworks({ active: true });
    const { getUsersList } = useUsers({ active: true });
    const { getStatesList, getStatesListWithGroups } = useStates({ active: true });
    const service = new DepartmentService();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [data] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getDepartmentById(id as string);
        return response.getResponseData() as Department;
    }, [id]));

    const mode = data ? 'Editar' : 'Crear';

    const submit = async (values: any) => {
        setIsLoading(true);

        if (mode === 'Crear') {
            try {
                const response = await service.createDepartment(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    toast.success("Departamento creado correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            try {
                const response = await service.editDepartment(values);
                const responseData = response.getResponseData();

                if (responseData.success) {
                    toast.success("Departamento editado correctamente");
                    refetch();
                    setIsOpen(false);
                } else {
                    responseData.data.errors.forEach((error: any) => {
                        toast.error(error.message);
                    });
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const deleteDepartment = useDeleteDepartment(refetch);

    const formik = useFormik<DepartmentFieldsModel>({
        initialValues: {
            departmentId: id ?? '',
            name: data?.name ?? '',
            description: data?.description ?? '',
            handlesCommunications: data?.handlesCommunications ?? false,
            commercialNetworkIds: data?.commercialNetworks?.map((n: any) => n.commercialNetwork?.id) ?? [],
            departmentUsers: data?.user?.map((u: any) => u.id) ?? [],
            departmentStatusesToView: data?.departmentsHasStatuses?.filter((s: any) => s.action === 'view').map((s: any) => s.statuses?.id) ?? [],
            departmentStatusesToEdit: data?.departmentsHasStatuses?.filter((s: any) => s.action === 'edit').map((s: any) => s.statuses?.id) ?? [],
            departmentDashboardStatuses: data?.departmentsHasStatuses?.filter((s: any) => s.action === 'dashboard').map((s: any) => s.statuses?.id) ?? [],
            departmentBlockedContractStatuses: data?.departmentsHasStatuses?.filter((s: any) => s.action === 'blocked').map((s: any) => s.statuses?.id) ?? [],
            departmentProductTypes: data?.departmentsHasProductTypes?.map((p: any) => p.productType?.id) ?? [],
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => {
            // Remove group options for states selectors
            values.departmentStatusesToView = (values.departmentStatusesToView || []).filter((id: string) => !id.includes('__group__'));
            values.departmentStatusesToEdit = (values.departmentStatusesToEdit || []).filter((id: string) => !id.includes('__group__'));
            values.departmentDashboardStatuses = (values.departmentDashboardStatuses || []).filter((id: string) => !id.includes('__group__'));
            values.departmentBlockedContractStatuses = (values.departmentBlockedContractStatuses || []).filter((id: string) => !id.includes('__group__'));
            submit(values);
        },
    });

    useEffect(() => {
        if (data) {
            formik.setValues({
                departmentId: data.id,
                name: data.name,
                description: data.description,
                handlesCommunications: data.handlesCommunications ?? false,
                commercialNetworkIds: data.commercialNetworks?.map((n: any) => n.commercialNetwork?.id) ?? [],
                departmentUsers: data.user?.map((u: any) => u.id) ?? [],
                departmentStatusesToView: data.departmentsHasStatuses?.filter((s: any) => s.action === 'view').map((s: any) => s.statuses?.id) ?? [],
                departmentStatusesToEdit: data.departmentsHasStatuses?.filter((s: any) => s.action === 'edit').map((s: any) => s.statuses?.id) ?? [],
                departmentDashboardStatuses: data.departmentsHasStatuses?.filter((s: any) => s.action === 'dashboard').map((s: any) => s.statuses?.id) ?? [],
                departmentBlockedContractStatuses: data.departmentsHasStatuses?.filter((s: any) => s.action === 'blocked').map((s: any) => s.statuses?.id) ?? [],
                departmentProductTypes: data.departmentsHasProductTypes?.map((p: any) => p.productType?.id) ?? [],
            });
        }
    }, [data]);

    useEffect(() => {
        if (id === undefined) {
            formik.resetForm();
        }
        return () => {
            formik.resetForm();
        }
    }, [id]);

    if (isOpen && id && !data) return <Loader />;

    const sortStatesByEntityAndOrder = (a: any, b: any) => {
        if (a.entityLabel < b.entityLabel) return -1;
        if (a.entityLabel > b.entityLabel) return 1;
        if (a.order < b.order) return -1;
        if (a.order > b.order) return 1;
        return 0;
    };

    const getSelectedDepartmentStates = (selectedIds?: string[] | null) => {
        if (!selectedIds) return [];
        return getStatesList()
            .filter((state: any) => selectedIds.includes(state.value))
            .sort(sortStatesByEntityAndOrder);
    };

    const renderDepartmentStatusOption = (option: any, meta: any) => {
        if (meta?.context !== 'value') return option.label;

        return (
            <span className="flex max-w-full items-center gap-1.5">
                <span className="shrink-0 rounded bg-white/85 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                    {option.entityLabel || 'Estado'}
                </span>
                <span className="truncate font-medium">
                    {option.statusLabel || option.label}
                </span>
            </span>
        );
    };

    const handleGroupedStatusesChange = (
        fieldName: 'departmentStatusesToView' | 'departmentStatusesToEdit' | 'departmentDashboardStatuses',
        selectedOptions: any
    ) => {
        if (!selectedOptions) {
            formik.setFieldValue(fieldName, []);
            return;
        }

        const groupOption = selectedOptions.find((option: any) => option.isGroup);

        if (groupOption) {
            const entityStates = getStatesList().filter((state: any) => state.entity === groupOption.entity);
            const entityStateIds = entityStates.map((state: any) => state.value);
            const currentSelections = (formik.values[fieldName] as string[] | undefined) || [];
            const otherSelections = currentSelections.filter((id: string) => !entityStateIds.includes(id));
            const isAdding = !entityStateIds.every((id: string) => currentSelections.includes(id));

            formik.setFieldValue(
                fieldName,
                isAdding ? [...otherSelections, ...entityStateIds] : otherSelections
            );
            return;
        }

        formik.setFieldValue(
            fieldName,
            selectedOptions.map((option: any) => option.value)
        );
    };

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-10 grid gap-3 @2xl:gap-9 @3xl:gap-11 font-medium">
                        {id && isOpen && (
                            <div className="flex justify-between items-start gap-4 pt-2 pb-2 border-b border-gray-200 mb-4">
                                <div>
                                    <div className="text-dark-500 font-medium">Información General</div>
                                    <div className="text-sm text-gray-500">Datos del departamento</div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <Button
                                        onClick={() => setCopyConfig(true)}
                                        color='secondary'
                                        variant="text"
                                    >
                                        <FaRegCopy size={20} />
                                    </Button>

                                    {userCan('get_departments', 'departments') && (
                                        <Button
                                            onClick={() => setInfo(true)}
                                            color="secondary"
                                            variant="text"
                                        >
                                            <FaInfoCircle size={20} />
                                        </Button>
                                    )}

                                    {userCan('delete_departments', 'departments') && (
                                        <ButtonPopover
                                            title="Eliminar departamento"
                                            description="Esto eliminará todo lo que dependa de este departamento"
                                            onClick={() => { deleteDepartment(id); setIsOpen(false); setNodeId(undefined); }}
                                            variant="text"
                                            mobileView
                                            btnClassName="px-4 py-2 gap-2"
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            formik={formik}
                        />

                        <div className="rounded-md border border-gray-200 px-4 py-3">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <Text className="text-sm font-medium text-gray-900">Atiende comunicaciones</Text>
                                    <Text className="text-xs text-gray-500">
                                        Si está activo, el departamento aparecerá como destinatario al abrir comunicaciones y sus usuarios podrán atenderlas.
                                    </Text>
                                </div>
                                <Switch
                                    checked={Boolean(formik.values.handlesCommunications)}
                                    onChange={() => formik.setFieldValue('handlesCommunications', !formik.values.handlesCommunications)}
                                />
                            </div>
                        </div>

                        <div className="[&_.rizzui-input-label:empty]:!hidden [&_.rizzui-input-label:empty]:!mb-0">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                <label htmlFor="commercialNetworkIds" className="rizzui-input-label block text-sm font-medium">
                                    Redes Comerciales <span className="text-red-500">*</span>
                                </label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const allIds = getNetworksList().map((n: any) => n.value);
                                        formik.setFieldValue('commercialNetworkIds', allIds);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    disabled={getNetworksList().length === 0}
                                >
                                    Agregar todas
                                </Button>
                            </div>
                            <CustomSelect
                                isMulti
                                isSearchable
                                isClearable
                                required={false}
                                id={'commercialNetworkIds'}
                                label={null}
                                value={formik.values.commercialNetworkIds ? getNetworksList().filter((n: any) => formik.values.commercialNetworkIds?.includes(n.value)) : []}
                                options={getNetworksList()}
                                onChange={(e: any) => {
                                    const selectedIds = e ? e.map((option: any) => option.value) : [];
                                    formik.setFieldValue('commercialNetworkIds', selectedIds);
                                }}
                                error={formik.errors.commercialNetworkIds}
                            />
                        </div>

                        <div className="space-y-2">
                            <CustomSelect
                                isMulti
                                isSearchable
                                id={'departmentUsers'}
                                label="Usuarios"
                                value={formik.values.departmentUsers ? getUsersList().filter((u: any) => formik.values.departmentUsers?.includes(u.value)) : []}
                                options={getUsersList()}
                                onChange={(e: any) => {
                                    const selectedIds = e ? e.map((option: any) => option.value) : [];
                                    formik.setFieldValue('departmentUsers', selectedIds);
                                }}
                                isDisabled={!userCan('get_users_for_select', 'user')}
                            />
                            {!userCan('get_users_for_select', 'user') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los usuarios</span>
                                </div>
                            )}
                        </div>

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'departmentStatusesToView'}
                            label="Estados que puede visualizar"
                            info="Muestran al usuario aquellos contratos que se encuentran en un estado concreto de los seleccionados. Si no selecciona ninguno, podrá visualizar todos los estados."
                            value={getSelectedDepartmentStates(formik.values.departmentStatusesToView)}
                            options={getStatesListWithGroups()}
                            formatOptionLabel={renderDepartmentStatusOption}
                            isDisabled={!userCan('list_statuses', 'statuses')}
                            onChange={(e: any) => handleGroupedStatusesChange('departmentStatusesToView', e)}
                        />

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'departmentStatusesToEdit'}
                            label="Estados que puede modificar"
                            info="Permiten al usuario modificar el estado de un contrato a uno de los seleccionados. Si no se selecciona ninguno, podrá modificar a cualquier estado."
                            value={getSelectedDepartmentStates(formik.values.departmentStatusesToEdit)}
                            options={getStatesListWithGroups()}
                            formatOptionLabel={renderDepartmentStatusOption}
                            onChange={(e: any) => handleGroupedStatusesChange('departmentStatusesToEdit', e)}
                        />

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'departmentDashboardStatuses'}
                            label="Estados visibles en dashboard"
                            info="Limita los contadores del bloque de contratos por estado del dashboard a los estados seleccionados. Si el usuario no tiene permiso para ver el dashboard completo y no hay estados configurados, este bloque mostrará un aviso sin datos."
                            value={getSelectedDepartmentStates(formik.values.departmentDashboardStatuses)}
                            options={getStatesListWithGroups()}
                            formatOptionLabel={renderDepartmentStatusOption}
                            isDisabled={!userCan('list_statuses', 'statuses')}
                            onChange={(e: any) => handleGroupedStatusesChange('departmentDashboardStatuses', e)}
                        />

                        <CustomSelect
                            isMulti
                            isSearchable
                            id={'departmentBlockedContractStatuses'}
                            label="Estados de contrato bloqueados para producto"
                            info="Mientras el contrato esté en uno de estos estados, los usuarios del departamento no podrán modificar el estado de producto."
                            value={getStatesList('contract').filter((s: any) => formik.values.departmentBlockedContractStatuses?.includes(s.value))}
                            options={getStatesList('contract')}
                            formatOptionLabel={renderDepartmentStatusOption}
                            isDisabled={!userCan('list_statuses', 'statuses')}
                            onChange={(e: any) => {
                                const selectedIds = e ? e.map((option: any) => option.value) : [];
                                formik.setFieldValue('departmentBlockedContractStatuses', selectedIds);
                            }}
                        />

                        <div className="space-y-2">
                            <CustomSelectApiHookForm
                                id="departmentProductTypes"
                                label="Tipos de producto"
                                isMulti
                                formik={formik}
                                fetchOptions={async (searchText: string | null) => {
                                    const service = new ProductTypeService();
                                    const response = await service.listProductTypesForSelect({
                                        limit: 200,
                                        filter_filters: {
                                            search_text: searchText || undefined,
                                            ids: !searchText && (formik.values?.departmentProductTypes?.length ?? 0) > 0
                                                ? formik.values.departmentProductTypes
                                                : undefined,
                                        },
                                    });
                                    const fetched = response.getResponseData()?.data;
                                    const rows = fetched?.data ?? [];
                                    return rows.map((r: { id: string; name: string }) => ({
                                        value: r.id,
                                        label: r.name || r.id,
                                    })) as any;
                                }}
                                isDisabled={!userCan('get_product_types_for_select', 'product_types')}
                                error={formik.errors.departmentProductTypes}
                                minInputLength={0}
                            />
                            {!userCan('get_product_types_for_select', 'product_types') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los tipos de producto</span>
                                </div>
                            )}
                        </div>
                       

                        <Textarea
                            id='description'
                            label="Descripción"
                            className="[&>label>span]:font-medium"
                            rows={3}
                            onChange={formik.handleChange}
                            value={formik.values.description}
                            error={formik.errors.description}
                        />
                    </div>
                </form>
            </div>
        )
    };

    return (
        <>
            {isOpen && (
                <>
                    <Page container="fluid">
                        {(mode === "Editar" && data) && getContent()}
                        {mode === "Crear" && getContent()}
                    </Page>
                    <FormFooter
                        customClassName={"border-0 py-1"}
                        customBg={"bg-transparent"}
                        submitBtnText={mode + ' Departamento'}
                        handleCancelBtn={() => { setIsOpen(false); setNodeId(undefined); formik.resetForm(); }}
                        handleSubmitBtn={formik.submitForm} isLoading={isLoading}
                    />
                </>
            )}
        </>
    )
};

export default DepartmentForm;
